import type { RootOptions } from 'react-dom/client';
import { type Attributes, context, type Span, trace } from '@opentelemetry/api';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
} from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BrowserSpanProcessor } from './browser-span-processor.js';
import { createErrorReporter } from './error-reporter.js';
import { ScrubbingSpanExporter } from './scrub.js';
import { noopLogger, type TelemetryLogger, type ThrownValue } from './types.js';
import { runInSpan } from './with-span.js';

const TRACER_NAME = '@trinity/observability-web';

export type WebTelemetryOptions = {
  /** Everything is a no-op when false, so apps can ship the code and switch it on per environment. */
  enabled: boolean;
  serviceName: string;
  serviceVersion?: string;
  environment: string;
  /** Full URL of the OTLP/HTTP traces relay on our API (the browser never talks to a collector directly). */
  tracesEndpoint: string;
  /** Fraction of page loads and requests recorded. The browser is the trace root, so this also drives backend volume. */
  sampleRatio: number;
  /** Where trace headers may be sent: our own API origins only. Other origins get spans but no headers. */
  propagateTraceHeaderTo: ReadonlyArray<string | RegExp>;
  /** URLs that must never be traced, e.g. error trackers. The telemetry endpoint is always excluded. */
  ignoreUrls?: ReadonlyArray<string | RegExp>;
  /** Record a span for every asset a page load fetches (100+ per load). Off by default. */
  recordResourceSpans?: boolean;
};

type NavigationSpan = { end: (route: string | undefined) => void };

export type SpanOptions = { name: string; op?: string; attributes?: Attributes };

export type WebTelemetry = {
  logger: TelemetryLogger;
  /** `createRoot` options reporting React errors (uncaught, caught by a boundary, and recoverable). */
  rootErrorOptions: Pick<RootOptions, 'onUncaughtError' | 'onCaughtError' | 'onRecoverableError'>;
  /** Times a route transition. Call `end` with the matched route pattern (not the concrete URL) when it settles. */
  startNavigation: () => NavigationSpan;
  /**
   * Runs `fn` inside a span and returns its result (same call shape as `Sentry.startSpan`). Async functions are
   * awaited, so the span covers the whole call; a thrown or rejected error is recorded on the span and re-thrown.
   * `op` is kept as the `app.op` attribute.
   */
  startSpan: <T>(options: SpanOptions, fn: () => T) => T;
  /** Ids of the active span, e.g. to tag another tool's events so they can be found from a trace. */
  getTraceContext: () => { traceId: string; spanId: string } | undefined;
  shutdown: () => Promise<void>;
};

const noopNavigation: NavigationSpan = { end: () => undefined };

const noopTelemetry: WebTelemetry = {
  logger: noopLogger,
  rootErrorOptions: {},
  startNavigation: () => noopNavigation,
  startSpan: (_options, fn) => fn(),
  getTraceContext: () => undefined,
  shutdown: () => Promise.resolve(),
};

export function initWebTelemetry(options: WebTelemetryOptions): WebTelemetry {
  if (!options.enabled || typeof window === 'undefined') {
    return noopTelemetry;
  }

  const exporter = new ScrubbingSpanExporter(new OTLPTraceExporter({ url: options.tracesEndpoint }));
  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      'service.name': options.serviceName,
      'service.version': options.serviceVersion,
      'deployment.environment.name': options.environment,
    }),
    sampler: new ParentBasedSampler({ root: new TraceIdRatioBasedSampler(options.sampleRatio) }),
    spanProcessors: [
      new BrowserSpanProcessor(
        // Small, infrequent batches: this runs on users' devices alongside the app. The queue is roomy enough that a
        // busy page load cannot push out its root span, which ends last.
        new BatchSpanProcessor(exporter, {
          maxQueueSize: 512,
          maxExportBatchSize: 50,
          scheduledDelayMillis: 5000,
          exportTimeoutMillis: 10_000,
        }),
        { recordResourceSpans: options.recordResourceSpans ?? false }
      ),
    ],
  });
  provider.register();

  const requestInstrumentationOptions = {
    propagateTraceHeaderCorsUrls: options.propagateTraceHeaderTo.map((target) =>
      typeof target === 'string'
        ? new RegExp(`^${target.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:[/?#]|$)`)
        : target
    ),
    ignoreUrls: [options.tracesEndpoint, ...(options.ignoreUrls ?? [])],
    clearTimingResources: true,
  };
  registerInstrumentations({
    tracerProvider: provider,
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new XMLHttpRequestInstrumentation(requestInstrumentationOptions),
      new FetchInstrumentation(requestInstrumentationOptions),
    ],
  });

  const tracer = provider.getTracer(TRACER_NAME);
  const logger = createErrorReporter(tracer);

  const reportThrown = (error: ThrownValue, mechanism: string) => {
    logger.captureException(error, { tags: { mechanism }, level: 'error' });
  };

  // Errors nothing else caught. When Sentry is also enabled it has its own handlers; these report to OpenTelemetry only.
  window.addEventListener('error', (event) => reportThrown(event.error ?? event.message, 'window.onerror'));
  window.addEventListener('unhandledrejection', (event) => reportThrown(event.reason, 'unhandledrejection'));

  // Closing a tab or backgrounding the page may never get another chance to export.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void provider.forceFlush();
    }
  });

  return {
    logger,
    rootErrorOptions: {
      onUncaughtError: (error, errorInfo) =>
        logger.captureException(error, {
          tags: { mechanism: 'react.uncaught' },
          extra: { componentStack: errorInfo.componentStack },
        }),
      onCaughtError: (error, errorInfo) =>
        logger.captureException(error, {
          tags: { mechanism: 'react.boundary' },
          extra: { componentStack: errorInfo.componentStack },
        }),
      onRecoverableError: (error, errorInfo) =>
        logger.captureException(error, {
          tags: { mechanism: 'react.recoverable' },
          extra: { componentStack: errorInfo.componentStack },
        }),
    },
    startNavigation: () => {
      const span: Span = tracer.startSpan('navigation', {}, context.active());
      return {
        end: (route) => {
          if (route) span.setAttribute('app.route', route);
          span.end();
        },
      };
    },
    startSpan: (options, fn) =>
      runInSpan(tracer, options.name, fn, {
        ...options.attributes,
        ...(options.op ? { 'app.op': options.op } : {}),
      }),
    getTraceContext: () => {
      const spanContext = trace.getActiveSpan()?.spanContext();
      return spanContext && trace.isSpanContextValid(spanContext)
        ? { traceId: spanContext.traceId, spanId: spanContext.spanId }
        : undefined;
    },
    shutdown: () => provider.shutdown(),
  };
}
